import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {SQL_TEMPLATE_FORM_HEIGHT} from 'insights/InsightsConstants';
import {GRAY_DARK} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {ElementMouseEvent} from 'util/Types';

import {FORBIDDEN_COLUMN_ALIAS_STRINGS} from './SQLTemplateFormConstants';
import {AvailableColumn, AvailableColumnProperties} from './SQLTemplateFormTypes';
import {getColumnIcon, validateFormValues} from './SQLTemplateFormUtil';

export interface SQLColumnSelectorTableProps {
  availableColumns: Array<AvailableColumn>;
  updateColumnState: (columnIndex: number, columnProperties: AvailableColumnProperties) => void;
  updateColumnStatesInRange: (
    startIndex: number,
    stopIndex: number,
    columnProperties: AvailableColumnProperties
  ) => void;
  updateAllColumnStates: (columnProperties: AvailableColumnProperties) => void;
}

export default function SQLColumnSelectorTable(props: SQLColumnSelectorTableProps): JSX.Element {
  const inputDisabled = props.availableColumns.length === 0;
  // Used to track which column to select between when shift-selecting checkboxes
  const [anchorRow, setAnchorRow] = useState<number | null>(null);

  function setColumnAlias(columnIndex: number, newAlias: string): void {
    props.updateColumnState(columnIndex, {
      alias: newAlias.length > 0 ? newAlias : null,
      selected: true,
      validationError: validateFormValues([newAlias], 'Display name', FORBIDDEN_COLUMN_ALIAS_STRINGS),
    });
  }

  function handleSelection(columnIndex: number, selecting: boolean, shiftKey: boolean): void {
    if (shiftKey && anchorRow != null) {
      const startIndex = Math.min(anchorRow, columnIndex);
      const stopIndex = Math.max(anchorRow, columnIndex);
      props.updateColumnStatesInRange(startIndex, stopIndex, {selected: selecting});
    } else {
      props.updateColumnState(columnIndex, {selected: selecting});
    }
    setAnchorRow(columnIndex);
  }

  return (
    <Table
      className={classNames('u-no-select', css(styles.table))}
      emptyTableText="No fields available."
      numRows={props.availableColumns.length}
      frozenHeader={true}
      overflowVisible={true}
      onRowClick={(rowIndex: number, e: ElementMouseEvent) => {
        handleSelection(rowIndex, !props.availableColumns[rowIndex].selected, e.shiftKey);
      }}
    >
      <Column
        header={
          <Cell className="simpleTable-checkboxCell">
            <IndeterminateCheckbox
              disabled={inputDisabled}
              items={props.availableColumns}
              selectedMap={(column) => column.selected}
              onChange={(e) => {
                props.updateAllColumnStates({selected: e.target.checked});
              }}
            />
          </Cell>
        }
        renderCell={(rowIndex) => (
          <Cell className="simpleTable-checkboxCell">
            <input
              checked={props.availableColumns[rowIndex].selected}
              disabled={inputDisabled}
              // Pass since this is a controlled checkbox. readOnly does not change the styling of the
              // checkbox, but fulfills React's prop requirements of checkboxes without an onChange prop.
              readOnly={true}
              type="checkbox"
            />
          </Cell>
        )}
      />
      <Column
        header={<Cell>Field</Cell>}
        renderCell={(rowIndex) => (
          <Cell>
            <BenchlingIcon
              className={css(styles.typeIcon)}
              icon={getColumnIcon(props.availableColumns[rowIndex])}
            />
            <span className="u-mini-spacing-left">{props.availableColumns[rowIndex].columnData.name}</span>
          </Cell>
        )}
      />
      <Column
        header={<Cell>Display name</Cell>}
        renderCell={(rowIndex) => {
          const validationError = props.availableColumns[rowIndex].validationError;
          return (
            <Cell>
              <TextInput
                className={css(styles.aliasInput)}
                disabled={inputDisabled}
                onClick={(e) => {
                  // Prevent toggling whether the column is selected by clicking into the alias field
                  e.stopPropagation();
                }}
                hasError={validationError != null}
                onChange={(e) => {
                  setColumnAlias(rowIndex, e.target.value);
                }}
                size={Size.SMALL}
                value={props.availableColumns[rowIndex].alias || ''}
              />
              <FieldError compact={true} message={validationError} />
            </Cell>
          );
        }}
      />
    </Table>
  );
}

const styles = StyleSheet.create({
  aliasInput: {
    width: 200,
  },
  table: {
    overflowY: 'scroll',
    // Subtract room for header and bottom padding
    maxHeight: SQL_TEMPLATE_FORM_HEIGHT - 125,
  },
  typeIcon: {
    color: GRAY_DARK,
  },
});
